/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Node } from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import classnames from 'classnames';

import { IRefinerHeaderProps, RefinerHeaderComponent } from './refiner-header.component';
import { RefinerChoiceSummaryComponent } from './refiner-choice-summary.component';
import { IRefinerFooterProps, RefinerFooterComponent } from './refiner-footer.component';
import {
    IRefinerModalProps,
    IRefinerModalToggleProps,
    RefinerModalComponent,
    RefinerModalToggleComponent
} from './refiner-modal.component';

/**
 * Class names used in component
 */
const componentClassName: string = 'msc-refiner';
const refinerContentWrapperClassName: string = `${componentClassName}__wrapper`;
const refinerOptionsWrapperClassName: string = `${componentClassName}__wrapper-internal`;
const modalToggleWrapperClassName: string = `${componentClassName}__wrapper-refiner-modal-toggle`;
const modalToggleMoreActionsWrapperClassName = `${modalToggleWrapperClassName}__wrapper-more-actions`;
const modalToggleExtraActionsWrapperClassName = `${modalToggleWrapperClassName}__wrapper-extra-actions`;

/**
 * IRefinerComponent interface.
 */
interface IRefinerComponent extends IComponent<IRefinerProps> {}

/**
 * IRefinerProps interface.
 */
export interface IRefinerProps extends IComponentProps<{}> {
    className?: string;
    refinerModalToggleProps?: IRefinerModalToggleProps;
    refinerModalProps?: IRefinerModalProps;
    refinerHeaderProps?: IRefinerHeaderProps;
    refinerFooterProps?: IRefinerFooterProps;
    refinerOptions?: React.ReactNode[];
    isMobileView?: boolean;
    isExpanded?: boolean;
}

function createComponentInMobileView(props: IRefinerProps): React.ReactNode {
    const refinerChoiceSummaryProps = props.refinerHeaderProps?.refinerChoiceSummaryProps;

    return (
        <>
            {refinerChoiceSummaryProps && <RefinerChoiceSummaryComponent {...refinerChoiceSummaryProps} />}
            {props.refinerModalToggleProps && (
                <div className={modalToggleWrapperClassName}>
                    <RefinerModalToggleComponent {...props.refinerModalToggleProps} />
                    <div className={modalToggleMoreActionsWrapperClassName}>
                        {props.refinerHeaderProps?.extraActions && (
                            <div className={modalToggleExtraActionsWrapperClassName}>{props.refinerHeaderProps.extraActions}</div>
                        )}
                    </div>
                </div>
            )}
            {props.refinerModalProps && <RefinerModalComponent {...props.refinerModalProps} />}
        </>
    );
}

function createComponentInDesktopView(props: IRefinerProps): React.ReactNode {
    const { refinerOptions, isExpanded } = props;

    return (
        <>
            {props.refinerHeaderProps && <RefinerHeaderComponent {...props.refinerHeaderProps} />}

            {refinerOptions && ArrayExtensions.hasElements(refinerOptions) && isExpanded && (
                <Node className={refinerContentWrapperClassName}>
                    <Node className={refinerOptionsWrapperClassName}>
                        {refinerOptions.map((item, index) => (
                            <React.Fragment key={index}>{item}</React.Fragment>
                        ))}
                    </Node>

                    {props.refinerFooterProps && refinerOptions && ArrayExtensions.hasElements(refinerOptions) && isExpanded && (
                        <RefinerFooterComponent {...props.refinerFooterProps} />
                    )}
                </Node>
            )}
        </>
    );
}

/**
 * Refiner component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerFunctionalComponent: React.FC<IRefinerProps> = (props: IRefinerProps) => {
    const renderMobile = props.isMobileView && props.refinerModalProps;

    return (
        <Node className={classnames(props.className, componentClassName)}>
            {renderMobile && createComponentInMobileView(props)}
            {!renderMobile && createComponentInDesktopView(props)}
        </Node>
    );
};

/**
 * Refiner component.
 */
export const RefinerComponent: React.FunctionComponent<IRefinerProps> = msdyn365Commerce.createComponent<IRefinerComponent>('Refiner', {
    component: React.memo(RefinerFunctionalComponent)
});
